import { Offcanvas } from "bootstrap";

/**
 * Gestionnaire global d'offcanvas pour l'application
 * Permet de créer, afficher et gérer des offcanvas avec une API cohérente
 */
class OffcanvasManager {
    /**
     * Crée et retourne une instance du gestionnaire d'offcanvas
     * @returns {OffcanvasManager} L'instance singleton
     */
    static getInstance() {
        if (!OffcanvasManager.instance) {
            OffcanvasManager.instance = new OffcanvasManager();
        }
        return OffcanvasManager.instance;
    }

    constructor() {
        // Map pour stocker les instances d'offcanvas
        this.instances = new Map();
        
        // Configuration par défaut
        this.defaultOptions = {
            backdrop: true,
            keyboard: true,
            scroll: false
        };
    }

    /**
     * Récupère ou crée une instance d'offcanvas
     * @param {string} elementId - ID de l'élément offcanvas
     * @param {Object} options - Options Bootstrap Offcanvas
     * @returns {Offcanvas} Instance Bootstrap Offcanvas
     */
    getInstance(elementId, options = {}) {
        // Vérifier si l'instance existe déjà dans notre Map
        if (this.instances.has(elementId)) {
            return this.instances.get(elementId);
        }
        
        // Récupérer l'élément du DOM
        const element = document.getElementById(elementId);
        if (!element) {
            console.error(`L'élément offcanvas ${elementId} n'a pas été trouvé`);
            return null;
        }
        
        // Vérifier si Bootstrap a déjà une instance pour cet élément
        let instance = Offcanvas.getInstance(element);
        
        // Si non, créer une nouvelle instance avec les options
        if (!instance) {
            const mergedOptions = { ...this.defaultOptions, ...options };
            instance = new Offcanvas(element, mergedOptions);
        }
        
        // Stocker l'instance dans notre Map
        this.instances.set(elementId, instance);
        
        return instance;
    }

    /**
     * Affiche un offcanvas
     * @param {string} elementId - ID de l'élément offcanvas
     * @param {Object} options - Options Bootstrap Offcanvas
     */
    show(elementId, options = {}) {
        const instance = this.getInstance(elementId, options);
        if (instance && !instance._isShown) {
            instance.show();
        }
    }

    /**
     * Masque un offcanvas
     * @param {string} elementId - ID de l'élément offcanvas
     */
    hide(elementId) {
        const instance = this.getInstance(elementId);
        if (instance && instance._isShown) {
            instance.hide();
        }
    }

    /**
     * Bascule l'état d'un offcanvas (affiche/masque)
     * @param {string} elementId - ID de l'élément offcanvas
     * @param {Object} options - Options Bootstrap Offcanvas
     */
    toggle(elementId, options = {}) {
        const instance = this.getInstance(elementId, options);
        if (instance) {
            instance.toggle();
        }
    }

    /**
     * Met à jour le contenu d'un offcanvas
     * @param {string} elementId - ID de l'élément offcanvas
     * @param {Object} content - Configuration du contenu
     * @param {string} content.title - Titre à afficher dans l'en-tête
     * @param {string|HTMLElement} content.body - Contenu HTML ou élément à afficher
     * @param {Function} content.callback - Fonction à exécuter après mise à jour
     */
    updateContent(elementId, content = {}) {
        const { title, body, callback } = content;
        
        // Récupérer l'élément du DOM
        const element = document.getElementById(elementId);
        if (!element) {
            console.error(`L'élément offcanvas ${elementId} n'a pas été trouvé`);
            return;
        }
        
        // Mettre à jour le titre si fourni
        if (title) {
            const titleElement = element.querySelector('.offcanvas-title');
            if (titleElement) {
                titleElement.innerHTML = title;
            }
        }
        
        // Mettre à jour le contenu si fourni
        if (body) {
            const bodyElement = element.querySelector('.offcanvas-body');
            if (bodyElement) {
                if (typeof body === 'string') {
                    bodyElement.innerHTML = body;
                } else if (body instanceof HTMLElement) {
                    bodyElement.innerHTML = '';
                    bodyElement.appendChild(body);
                }
            }
        }
        
        // Exécuter le callback si fourni
        if (typeof callback === 'function') {
            callback(element);
        }
    }

    /**
     * Dispose une instance d'offcanvas
     * @param {string} elementId - ID de l'élément offcanvas
     */
    dispose(elementId) {
        if (this.instances.has(elementId)) {
            const instance = this.instances.get(elementId);
            if (instance) {
                instance.dispose();
            }
            this.instances.delete(elementId);
        }
    }

    /**
     * Dispose toutes les instances d'offcanvas
     */
    disposeAll() {
        for (const [elementId, instance] of this.instances.entries()) {
            if (instance) {
                try {
                    instance.dispose();
                } catch (e) {
                    console.warn(`Erreur lors de la suppression de l'instance ${elementId}:`, e);
                }
            }
        }
        this.instances.clear();
    }
}

// Export de l'instance singleton
export default OffcanvasManager.getInstance();